function sizeGuideDrawer() {
  return {
    // Centimeter functionality
    centimeter: [],
    data: [],
    headers: [],
    active: "",

    // Body measurements functionality
    bodyCentimeter: [],
    bodyData: [],
    bodyHeaders: [],
    bodyActive: "",

    // Scroll conditions
    scrollConditions: {},

    // Existing drawer functionality
    isOpen: false,
    activeTab: "fit-sizing",

    toggle() {
      this.isOpen = !this.isOpen;
    },

    init() {
      // Initialize centimeter functionality if data is available
      this.initializeCentimeterData();
      this.initializeBodyMeasurementsData();

      // Add window resize listener
      this.resizeHandler = () => {
        if (this.isOpen) {
          this.updateScrollConditions();
        }
      };
      window.addEventListener("resize", this.resizeHandler);

      this.$watch("isOpen", (value) => {
        if (value) {
          document.body.style.overflow = "hidden";
          // Re-initialize data when drawer opens
          this.$nextTick(() => {
            this.initializeCentimeterData();
            this.initializeBodyMeasurementsData();
            // Update scroll conditions after data is loaded
            this.$nextTick(() => {
              this.updateScrollConditions();
            });
          });
        } else {
          document.body.style.overflow = "";
        }
      });
    },

    destroy() {
      // Clean up event listeners
      if (this.resizeHandler) {
        window.removeEventListener("resize", this.resizeHandler);
      }
    },

    /**
     * Initialize centimeter data and headers
     */
    initializeCentimeterData() {
      // Check if we have centimeter data from the page
      if (window.productSizeGuideData && window.productSizeGuideData.length > 0) {
        this.centimeter = window.productSizeGuideData;

        // Extract headers from the first data item
        const firstItem = this.centimeter[0];

        // Transform object keys into headers
        this.headers = Object.keys(firstItem).map((key) => ({
          title: this.formatHeaderTitle(key),
          transformed_title: key
        }));

        // Set initial data based on market
        const isUSMarket = window.Shopify?.country === "US" || window.Shopify?.currency === "USD";
        this.active = isUSMarket ? "in" : "cm";
        this.data = isUSMarket ? this.convertToInchesData() : this.centimeter;

        // Force Alpine to re-render by triggering a reactive update
        this.$nextTick(() => {
          this.data = [...this.data];
          // Update scroll conditions after data changes
          this.$nextTick(() => {
            this.updateScrollConditions();
          });
        });
      }
    },

    /**
     * Initialize body measurements data and headers
     */
    initializeBodyMeasurementsData() {
      // Check if we have body measurements data from the page
      if (window.bodyMeasurementsData && window.bodyMeasurementsData.length > 0) {
        this.bodyCentimeter = window.bodyMeasurementsData;

        // Extract headers from the first data item
        const firstItem = this.bodyCentimeter[0];

        // Transform object keys into headers
        this.bodyHeaders = Object.keys(firstItem).map((key) => ({
          title: this.formatHeaderTitle(key),
          transformed_title: key
        }));

        // Set initial data based on market
        const isUSMarket = window.Shopify?.country === "US" || window.Shopify?.currency === "USD";
        this.bodyActive = isUSMarket ? "in" : "cm";
        this.bodyData = isUSMarket ? this.convertBodyToInchesData() : this.bodyCentimeter;

        // Force Alpine to re-render by triggering a reactive update
        this.$nextTick(() => {
          this.bodyData = [...this.bodyData];
          // Update scroll conditions after data changes
          this.$nextTick(() => {
            this.updateScrollConditions();
          });
        });
      }
    },

    /**
     * Format header title from snake_case to Title Case
     */
    formatHeaderTitle(key) {
      // Convert snake_case to Title Case
      return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },

    /**
     * Convert centimeters to inches
     */
    convertToInches(cmValue) {
      // Convert string to number if needed
      const cm = typeof cmValue === "string" ? parseFloat(cmValue) : cmValue;
      // Return empty string if not a valid number
      if (isNaN(cm)) return "";
      // Convert cm to inches (1 cm = 0.393701 inches) and round to 1 decimal
      return (cm * 0.393701).toFixed(1);
    },

    /**
     * Convert centimeter data to inches data
     */
    convertToInchesData() {
      if (!this.centimeter?.length > 0) return [];

      return this.centimeter.map((item) => {
        const inchesItem = {};
        Object.entries(item).forEach(([key, value]) => {
          // Keep size field unchanged, convert only measurement fields
          if (key.toLowerCase() === "size") {
            inchesItem[key] = value;
          } else {
            // Only convert numeric values for measurements
            inchesItem[key] = isNaN(value) ? value : this.convertToInches(value);
          }
        });
        return inchesItem;
      });
    },

    /**
     * Convert body measurements centimeter data to inches data
     */
    convertBodyToInchesData() {
      if (!this.bodyCentimeter?.length > 0) return [];

      return this.bodyCentimeter.map((item) => {
        const inchesItem = {};
        Object.entries(item).forEach(([key, value]) => {
          // Keep size field unchanged, convert only measurement fields
          if (key.toLowerCase() === "size") {
            inchesItem[key] = value;
          } else {
            // Only convert numeric values for measurements
            inchesItem[key] = isNaN(value) ? value : this.convertToInches(value);
          }
        });
        return inchesItem;
      });
    },

    /**
     * Set active unit (cm or inches)
     */
    setActive(unit) {
      if (unit === "cm" && this.centimeter?.length > 0) {
        this.active = "cm";
        this.data = this.centimeter;
      } else if (unit === "in" && this.centimeter?.length > 0) {
        this.active = "in";
        this.data = this.convertToInchesData();
      }
    },

    /**
     * Set active unit for body measurements (cm or inches)
     */
    setBodyActive(unit) {
      if (unit === "cm" && this.bodyCentimeter?.length > 0) {
        this.bodyActive = "cm";
        this.bodyData = this.bodyCentimeter;
      } else if (unit === "in" && this.bodyCentimeter?.length > 0) {
        this.bodyActive = "in";
        this.bodyData = this.convertBodyToInchesData();
      }
    },

    /**
     * Check if a container can be scrolled horizontally
     */
    canScroll(containerRef) {
      // Use cached value if available
      if (this.scrollConditions[containerRef] !== undefined) {
        return this.scrollConditions[containerRef];
      }

      const container = this.$refs[containerRef];
      if (!container) {
        return false;
      }

      // Check if content width exceeds container width
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;
      const canScroll = scrollWidth > clientWidth;

      this.scrollConditions[containerRef] = canScroll;
      return canScroll;
    },

    /**
     * Update scroll conditions for all containers
     */
    updateScrollConditions() {
      const containers = [
        "productSizeGuideContainer",
        "metaobjectSizeGuideContainer",
        "metaobjectBodyContainer",
        "bodyScrollContainer",
        "garmentScrollContainer"
      ];

      containers.forEach((containerRef) => {
        const container = this.$refs[containerRef];
        if (container) {
          const scrollWidth = container.scrollWidth;
          const clientWidth = container.clientWidth;
          const canScroll = scrollWidth > clientWidth;
          this.scrollConditions[containerRef] = canScroll;
        }
      });
    },

    /**
     * Scroll table container in specified direction
     */
    scrollTable(containerRef, direction) {
      const container = this.$refs[containerRef];
      if (!container) {
        console.warn(`Scroll container not found: ${containerRef}`);
        return;
      }

      const scrollAmount = Math.min(200, container.clientWidth * 0.8); // Responsive scroll amount
      const currentScroll = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;

      // Check if scroll is possible
      if (direction === "left" && currentScroll <= 0) {
        return;
      }

      if (direction === "right" && currentScroll >= maxScroll) {
        return;
      }

      // Calculate target scroll position
      const targetScroll =
        direction === "left"
          ? Math.max(0, currentScroll - scrollAmount)
          : Math.min(maxScroll, currentScroll + scrollAmount);

      // Perform smooth scroll
      container.scrollTo({
        left: targetScroll,
        behavior: "smooth"
      });
    }
  };
}

export default sizeGuideDrawer;
